import { DropOptions, NodeModel, TreeProps } from '@minoru/react-dnd-treeview';
import { CollectionTreeQuery } from 'lib-knowledge-management-common/graphql';
import {
  Article,
  Category,
  ParentRef,
} from 'lib-knowledge-management-common/models/types';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { useCallback } from 'react';

import useArticleMove from 'hooks/useArticleMove';
import useCategoryMove from 'hooks/useCategoryMove';

import { hasMatchingChildCategory } from '../utils/hasMatchingChildCategory';

import { useReduceTreeData } from './useReduceTreeData';

// NOTE: this will be shared with category move/add in a future PR.
export const CATEGORY_DEPTH_LIMIT = 5;

type DropTargetNode = Category | NonNullable<CollectionTreeQuery['collection']>;

export type NodeModelData = {
  node: Category | Article;
  index: number;
  depth: number;
};

export type MyNodeModel = NodeModel<NodeModelData>;

type UseDragAndDropReturn = {
  treeData: MyNodeModel[];
  onDrop: TreeProps<NodeModelData>['onDrop'];
  canDrop: TreeProps<NodeModelData>['canDrop'];
  loading: boolean;
};

export function useDragAndDrop(
  collectionData: CollectionTreeQuery | undefined,
): UseDragAndDropReturn {
  const { errorNotify } = useNotification();
  const [moveCategory, { loading: categoryMoveLoading }] = useCategoryMove();
  const [moveArticle, { loading: articleMoveLoading }] = useArticleMove();
  const treeData = useReduceTreeData(collectionData);
  const collection = collectionData?.collection;

  const handleMoveCategory = useCallback(
    (
      dropTargetNode: DropTargetNode,
      { dropTargetId, dragSource, relativeIndex }: DropOptions<NodeModelData>,
    ) => {
      if (dragSource?.data?.node.__typename !== 'Category')
        throw new Error('Category node not found in handleCategoryMove');

      if (!dropTargetNode.categories)
        throw new Error('Categories not found in dropTargetNode');

      const numSiblingCategories = dropTargetNode.categories.length;
      const categoryMoved = dragSource.data.node;

      const newParentRef: ParentRef =
        dropTargetId === 0
          ? {
              __typename: 'Collection',
              id: collection!.id as string,
            }
          : {
              __typename: 'Category',
              id: dropTargetId as string,
            };

      let position = (relativeIndex || 0) + 1;
      const hasMovedParent = dragSource.parent !== dropTargetId;

      // Adjust position if we're in the same parent.
      if (
        dragSource.parent === dropTargetId &&
        position - 1 > dragSource.data.index
      ) {
        position--;
      }

      const maxPosition = hasMovedParent
        ? numSiblingCategories + 1
        : numSiblingCategories;
      if (position > maxPosition) {
        position = maxPosition;
      }

      // Check if we've actually moved.
      if (
        dragSource.parent === dropTargetId &&
        dragSource.data.index === position - 1
      ) {
        return;
      }

      try {
        moveCategory({
          categoryId: categoryMoved.id,
          newParentRef: newParentRef,
          position,
        });
      } catch (error) {
        errorNotify({
          text: error instanceof Error ? error.message : 'Something went wrong',
          title: 'Unable to reorder categories',
        });
      }
      return;
    },
    [collection, moveCategory, errorNotify],
  );

  const handleMoveArticle = useCallback(
    (
      dropTargetNode: DropTargetNode,
      { dragSource, dropTargetId, relativeIndex }: DropOptions<NodeModelData>,
    ) => {
      if (dragSource?.data?.node.__typename !== 'Article')
        throw new Error('Article node not found in handleArticleMove');

      // This is prevented in canDrop, but just in case...
      if (dropTargetId === 0)
        throw new Error("Fix this! Can't move an article to the root!");

      const articleMoved = dragSource.data.node;

      const newParentRef: ParentRef = {
        __typename: 'Category',
        id: dropTargetId as string,
      };

      // Minus number of categories
      let position =
        (relativeIndex || 0) + 1 - (dropTargetNode.categories?.length || 0);

      // Adjust position if we're in the same parent.
      if (
        dragSource.parent === dropTargetId &&
        position - 1 > dragSource.data.index
      ) {
        position--;
      }

      // just safe guarding
      position = position < 1 ? 1 : position;

      // Check if we've actually moved.
      if (
        dragSource.parent === dropTargetId &&
        dragSource.data.index === position - 1
      ) {
        return;
      }

      try {
        moveArticle({
          articleId: articleMoved.id,
          newCategoryId: newParentRef.id,
          position,
        });
      } catch (error) {
        errorNotify({
          title: 'Unable to reorder articles',
          text: error instanceof Error ? error.message : 'Something went wrong',
        });
      }
      return;
    },
    [moveArticle, errorNotify],
  );

  const onDrop: UseDragAndDropReturn['onDrop'] = useCallback(
    (_newTreeData, opts) => {
      if (!collectionData || !collectionData.collection) return;

      const { dragSource, dropTargetId, dropTarget } = opts;

      // dropTargetId === 0 is the collection
      const dropTargetNode =
        dropTarget?.data?.node.__typename === 'Category'
          ? dropTarget.data.node
          : dropTargetId === 0
            ? collectionData.collection
            : undefined;

      if (!dropTargetNode) throw new Error('Drop target not found');

      if (dragSource?.data?.node.__typename === 'Category') {
        return handleMoveCategory(dropTargetNode, opts);
      }

      if (dragSource?.data?.node.__typename === 'Article') {
        return handleMoveArticle(dropTargetNode, opts);
      }
    },
    [collectionData, handleMoveArticle, handleMoveCategory],
  );

  const canDrop: UseDragAndDropReturn['canDrop'] = (
    _tree,
    { dragSource, dropTargetId, dropTarget, relativeIndex },
  ) => {
    // Prevent inconsistent state in the BE.
    if (categoryMoveLoading || articleMoveLoading) return false;

    // Prevent an article being dropped onto the collection.
    if (dragSource?.data?.node.__typename === 'Article' && dropTargetId === 0) {
      return false;
    }

    // You can always drop a category onto the collection.
    if (
      dragSource?.data?.node.__typename === 'Category' &&
      dropTargetId === 0
    ) {
      return true;
    }

    // Category depth check
    if (dragSource?.data?.node.__typename === 'Category') {
      if ((dropTarget?.data?.depth || 0) >= CATEGORY_DEPTH_LIMIT) return false;
    }

    // Prevent a category being dropped onto an article or vice versa.
    if (
      dropTarget?.data?.node.__typename === 'Category' &&
      dropTarget.data.node.categories &&
      typeof relativeIndex === 'number'
    ) {
      const numSubCategories = dropTarget.data.node.categories.length;

      // Make sure categories are always at the top
      if (
        dragSource?.data?.node.__typename === 'Article' &&
        relativeIndex < numSubCategories
      ) {
        return false;
      }

      // Make sure articles are always at the bottom
      if (
        dragSource?.data?.node.__typename === 'Category' &&
        relativeIndex > numSubCategories
      ) {
        return false;
      }
    }

    // Make sure you can't put a category into a child category.
    if (
      dragSource?.data?.node.__typename === 'Category' &&
      hasMatchingChildCategory(dragSource.data.node, dropTargetId as string)
    ) {
      return false;
    }

    // You can always drop in the same parent.
    if (dragSource?.parent === dropTargetId) {
      return true;
    }
  };

  return {
    onDrop,
    canDrop,
    loading: categoryMoveLoading || articleMoveLoading,
    treeData,
  };
}
